import { useState } from 'react';
import { SegmentedControl } from '../ui';
import { usePrimaryColor } from '../../branding/usePrimaryColor';
import { formatCurrency } from '../../utils/currency';
import type { RevenueBreakdownItem } from '../../types/dashboard';

type View = 'By service' | 'By provider';
const VIEW_OPTIONS: View[] = ['By service', 'By provider'];
const MAX_VISIBLE = 5;

interface RevenueBreakdownProps {
  byService: RevenueBreakdownItem[];
  byProvider: RevenueBreakdownItem[];
  loading?: boolean;
  periodLabel?: string;
}

export function RevenueBreakdown({
  byService,
  byProvider,
  loading = false,
  periodLabel,
}: RevenueBreakdownProps) {
  const [view, setView] = useState<View>('By service');
  const primaryColor = usePrimaryColor();
  const items = view === 'By service' ? byService : byProvider;

  // Loading skeleton
  if (loading && byService.length === 0 && byProvider.length === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b flex items-center justify-between">
          <div className="h-4 w-32 bg-canvas animate-pulse rounded" />
          <div className="h-6 w-40 bg-canvas animate-pulse rounded" />
        </div>
        <div className="px-4 pt-4 pb-3 space-y-3">
          {[1, 0.8, 0.6, 0.4].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-20 h-3 bg-canvas animate-pulse rounded shrink-0" />
              <div className="flex-1 h-6 bg-canvas animate-pulse rounded" style={{ width: `${100 - i * 18}%` }} />
              <div className="w-14 h-3 bg-canvas animate-pulse rounded shrink-0" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (byService.length === 0 && byProvider.length === 0 && !loading) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-[14px] font-semibold">Revenue breakdown</h3>
            {periodLabel && (
              <span className="text-[11.5px] text-ink-4">&middot; {periodLabel.toLowerCase()}</span>
            )}
          </div>
          <SegmentedControl
            options={[...VIEW_OPTIONS]}
            value={view}
            onChange={(v: string) => setView(v as View)}
          />
        </div>
        <div className="px-4 pt-4 pb-3">
          <div className="text-center py-8">
            <p className="text-[11.5px] text-ink-4">
              Revenue breakdown will appear after sessions are booked.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const visible = items.slice(0, MAX_VISIBLE);
  const remaining = items.length - MAX_VISIBLE;
  const maxTotal = Math.max(...items.map((d) => d.total), 1);

  return (
    <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 hair-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-[14px] font-semibold">Revenue breakdown</h3>
          {periodLabel && (
            <span className="text-[11.5px] text-ink-4">&middot; {periodLabel.toLowerCase()}</span>
          )}
        </div>
        <SegmentedControl
          options={[...VIEW_OPTIONS]}
          value={view}
          onChange={(v: string) => setView(v as View)}
        />
      </div>

      {/* Bar rows */}
      <div className="px-4 pt-4 pb-3 space-y-2.5">
        {visible.map((item, i) => {
          const widthPct = Math.max((item.total / maxTotal) * 100, item.total > 0 ? 3 : 0);
          // Decreasing opacity per row: 1.0, 0.8, 0.6, 0.45, 0.3
          const opacity = Math.max(1 - i * 0.18, 0.3);

          return (
            <div key={item.id} className="flex items-center gap-3">
              <span className="text-[11.5px] text-ink-3 w-24 truncate shrink-0" title={item.name}>
                {item.name}
              </span>
              <div className="flex-1 h-6 rounded overflow-hidden bg-canvas relative">
                <div
                  className="h-full rounded transition-all duration-300"
                  style={{
                    width: `${widthPct}%`,
                    backgroundColor: primaryColor,
                    opacity,
                  }}
                />
              </div>
              <span className="text-[12px] font-medium num shrink-0 w-16 text-right">
                ${formatCurrency(item.total)}
              </span>
            </div>
          );
        })}

        {remaining > 0 && (
          <div className="text-[10.5px] text-ink-4 pt-1">
            and {remaining} more
          </div>
        )}

        {/* Current-view empty hint */}
        {items.length === 0 && (byService.length > 0 || byProvider.length > 0) && (
          <div className="text-center py-6">
            <p className="text-[11.5px] text-ink-4">
              No {view === 'By service' ? 'service' : 'provider'} data for this period.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
